import { Dialog, Transition } from "@headlessui/react";
import { Icon } from "@iconify/react";
import clsx from "clsx";
import React, { Fragment, ReactNode } from "react";
import useCloseOnEscape from "../../hooks/useCloseOnEscape";

const preventImplicitClose = () => undefined;

const Modal = ({
  onClose,
  isOpen,
  children,
  title,
  maxWidth,
  noClose,
}: {
  onClose: () => void;
  isOpen: boolean;
  children: ReactNode;
  title?: string | ReactNode;
  maxWidth?: string;
  noClose?: boolean;
}) => {
  useCloseOnEscape(isOpen && !noClose, onClose);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[90]"
        onClose={preventImplicitClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden px-4 py-8 md:py-16">
          <div className="flex h-full justify-center text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                data-testid="modal-panel"
                className={clsx(
                  "flex max-h-full w-full self-start transform flex-col overflow-hidden rounded-xl bg-white text-left align-middle transition-all",
                  maxWidth ? maxWidth : "max-w-md"
                )}
              >
                <div className="flex shrink-0 items-center justify-between gap-4 border-b border-dark/10 px-5 py-4 md:px-6">
                  <Dialog.Title
                    as="h3"
                    className="flex grow text-base font-medium leading-6 text-gray-900 md:text-lg"
                  >
                    {title}
                  </Dialog.Title>

                  {noClose ? null : (
                    <button
                      type="button"
                      aria-label="Close modal"
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-0 bg-red-50 p-0 text-red-600 transition-colors hover:bg-red-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                      onClick={onClose}
                    >
                      <Icon
                        className="h-5 w-5"
                        icon="heroicons:x-mark"
                      />
                    </button>
                  )}
                </div>
                <div
                  data-testid="modal-scroll-content"
                  className="min-h-0 overflow-y-auto px-5 py-4 md:px-6 md:py-5"
                >
                  {children}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default Modal;
